import type { ReactNode } from "react";
import { Droplet, Hash, Info, Plus, Scale, X, type LucideIcon } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SupplierSelect } from "@/components/common/entity-select/SupplierSelect";
import { cn } from "@/lib/utils";
import { formatMoney, formatNumber } from "@/lib/materials/conversion-calc";
import {
  columnResult,
  converterUnitOptions,
  densityGPerMl,
  formatCaseResult,
  isColumnSet,
  packFillFor,
  parseConverterPack,
  recipeUnitChoices,
  uomLabel,
  withOzAnswer,
  withPackText,
  type ColumnResult,
  type ColumnState,
  type ConverterColumn,
  type ConverterState,
} from "@/lib/materials/conversion-grid";
import type { PackContainerOption } from "@/lib/materials/pack-size-parser";
import type { Uom } from "@/types/material-conversion";

interface PackSizeConverterProps {
  value: ConverterState;
  onChange: (next: ConverterState) => void;
  /** The whole `uoms` table; every dropdown here is a filter on its flags. */
  uoms: Uom[];
  containers: PackContainerOption[];
  readOnly?: boolean;
  /** The add/edit ingredient dialog picks one supplier for the ingredient, so it hides this one. */
  showSupplier?: boolean;
  fieldErrors?: Record<string, string[]>;
}

const COLUMN_ICON: Record<ConverterColumn, LucideIcon> = { EACH: Hash, WEIGHT: Scale, LIQUID: Droplet };

/** Above the add/edit ingredient dialog (z-60), which this also renders in. */
const POPUP_LAYER = "z-[70]";

/**
 * Pack size in, the units recipes use out ("Convert Pack Size to UoM used in
 * Recipes"). A case can be counted, weighed and measured at once — onions come
 * 30 to a 50 lb bag — so each has its own column, yield and recipe unit. A
 * pack size that reads cleanly fills its column; the rest are filled by hand.
 */
export function PackSizeConverter({
  value,
  onChange,
  uoms,
  containers,
  readOnly = false,
  showSupplier = true,
  fieldErrors = {},
}: PackSizeConverterProps) {
  const { t } = useLingui();
  const options = converterUnitOptions(uoms);
  const parse = parseConverterPack(value, uoms, containers);
  const fill = packFillFor(parse, uoms);
  const density = densityGPerMl(value, uoms);
  const results = {
    EACH: columnResult(value, "EACH", uoms, options),
    WEIGHT: columnResult(value, "WEIGHT", uoms, options),
    LIQUID: columnResult(value, "LIQUID", uoms, options),
  };

  const columnNames: Record<ConverterColumn, string> = { EACH: t`Each`, WEIGHT: t`Weighted`, LIQUID: t`Liquid` };
  const addLabels: Record<ConverterColumn, string> = { EACH: t`Add count`, WEIGHT: t`Add weight`, LIQUID: t`Add liquid size` };

  function setColumn(column: ConverterColumn, patch: Partial<ColumnState>) {
    onChange({ ...value, columns: { ...value.columns, [column]: { ...value.columns[column], ...patch } } });
  }

  function clearColumn(column: ConverterColumn) {
    setColumn(column, { perCase: "", yieldPct: "", recipeUomCode: "", open: false });
  }

  /** "From pack size" while the column still holds what the pack size said. */
  function isFromPack(column: ConverterColumn) {
    const col = value.columns[column];
    return fill?.column === column && fill.perCase === col.perCase && fill.unitCode === col.unitCode;
  }

  const error = (name: string) => fieldErrors[name]?.[0];

  return (
    <div className="flex flex-col gap-5">
      <div
        className={cn(
          "grid grid-cols-1 gap-4 sm:grid-cols-2",
          showSupplier ? "xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,0.7fr)]" : "xl:grid-cols-[minmax(0,1.3fr)_minmax(0,0.8fr)_minmax(0,0.7fr)]",
        )}
      >
        <LabeledField label={t`Pack size`} error={error("packSizeText")}>
          <div className="relative">
            <Input
              value={value.packText}
              onChange={(e) => onChange(withPackText(value, e.target.value, uoms, containers))}
              placeholder={t`50 lb bag`}
              disabled={readOnly}
              className="h-10 pr-28 text-base font-semibold placeholder:font-normal"
              aria-label={t`Pack size`}
            />
            <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
              <ParseStatusPill status={parse.status} />
            </span>
          </div>
        </LabeledField>
        {showSupplier && (
          <LabeledField label={t`Supplier`} error={error("supplierId")}>
            <SupplierSelect
              value={value.supplierId}
              onChange={(id) => onChange({ ...value, supplierId: id })}
              onClear={() => onChange({ ...value, supplierId: null })}
              disabled={readOnly}
            />
          </LabeledField>
        )}
        <LabeledField label={t`Supplier code`} error={error("supplierCode")}>
          <Input
            value={value.supplierCode}
            onChange={(e) => onChange({ ...value, supplierCode: e.target.value })}
            disabled={readOnly}
            className="h-10 font-mono"
            aria-label={t`Supplier code`}
          />
        </LabeledField>
        <LabeledField label={t`Case price`} error={error("casePrice")}>
          <div className="relative">
            <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-(--color-text-secondary)">$</span>
            <Input
              value={value.casePrice}
              onChange={(e) => onChange({ ...value, casePrice: e.target.value })}
              inputMode="decimal"
              disabled={readOnly}
              className="h-10 pl-7 font-semibold"
              aria-label={t`Case price`}
            />
          </div>
        </LabeledField>
      </div>

      {parse.status === "ambiguous" && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-(--color-warning-bg) p-4 text-(--color-warning-text)">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-semibold">{t`"OZ" can mean weight or liquid. Which is this?`}</span>
            <span className="text-sm">{t`Look for "FL" on the invoice or the bottle. This changes the cost per unit.`}</span>
          </div>
          <Button type="button" variant="outline" disabled={readOnly} onClick={() => onChange(withOzAnswer(value, "oz", uoms, containers))}>
            <Scale className="size-4" />
            {t`Weight`}
          </Button>
          <Button type="button" variant="outline" disabled={readOnly} onClick={() => onChange(withOzAnswer(value, "floz", uoms, containers))}>
            <Droplet className="size-4" />
            {t`Liquid`}
          </Button>
        </div>
      )}
      {parse.status === "error" && (
        <p className="text-sm text-(--color-text-secondary)">
          {t`We couldn't read this pack size. Fill in what a case holds below.`}
        </p>
      )}
      {parse.catchWeight && (
        <p className="text-sm text-(--color-text-secondary)">
          {t`Catch weight: the case weight is nominal until the delivery is weighed.`}
        </p>
      )}

      <div className="flex flex-col gap-3">
        <h3 className="text-lg font-bold text-(--color-text-primary)">{t`Convert Pack Size to UoM used in Recipes`}</h3>
        {error("eachPerCase") && <p className="text-sm text-(--color-danger-text)">{error("eachPerCase")}</p>}
        <div className="overflow-x-auto rounded-2xl border border-(--color-border)">
          {/* A cross-tab of rows × unit types, laid out as a grid rather than a
              record table: each cell holds its own inputs. */}
          <div className="grid min-w-[52rem] grid-cols-[9rem_repeat(3,minmax(0,1fr))]">
            <HeaderCell />
            {(["EACH", "WEIGHT", "LIQUID"] as const).map((column) => {
              const Icon = COLUMN_ICON[column];
              const set = isColumnSet(value, column);
              const fromPack = set && isFromPack(column);
              return (
                <HeaderCell key={column} bordered>
                  <Icon className="size-5 shrink-0" />
                  <span className="text-base font-bold">{columnNames[column]}</span>
                  <span className="ml-auto flex items-center gap-2">
                    <Badge variant={set ? "success" : "neutral"} className="px-2.5 py-0.5 text-xs">
                      {fromPack ? t`From pack size` : set ? t`Set` : t`Not set`}
                    </Badge>
                    {set && !fromPack && !readOnly && (
                      <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => clearColumn(column)}>
                        <X className="size-3.5" />
                        {t`Clear`}
                      </Button>
                    )}
                  </span>
                </HeaderCell>
              );
            })}

            {/* Per case */}
            <RowLabel>{t`Per case`}</RowLabel>
            {(["EACH", "WEIGHT", "LIQUID"] as const).map((column) => {
              const col = value.columns[column];
              const showInput = col.open || isColumnSet(value, column) || col.perCase !== "";
              const packUnits = column === "WEIGHT" ? options.weightPack : column === "LIQUID" ? options.liquidPack : [];
              return (
                <Cell key={column}>
                  {showInput ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        value={col.perCase}
                        onChange={(e) => setColumn(column, { perCase: e.target.value, yieldPct: col.yieldPct || "100", open: true })}
                        inputMode="decimal"
                        placeholder="0"
                        disabled={readOnly}
                        className="h-11 w-24 text-lg font-semibold"
                        aria-label={t`${columnNames[column]} per case`}
                      />
                      {column === "EACH" ? (
                        <span className="text-sm text-(--color-text-secondary)">{t`each`}</span>
                      ) : (
                        <UnitSelect
                          value={col.unitCode}
                          onChange={(code) => setColumn(column, { unitCode: code })}
                          units={packUnits}
                          disabled={readOnly}
                          label={t`${columnNames[column]} pack unit`}
                        />
                      )}
                      {column === "WEIGHT" && results.WEIGHT.basePerCase != null && col.unitCode !== "g" && (
                        <span className="text-sm text-(--color-text-secondary)">= {formatNumber(results.WEIGHT.basePerCase, 0)} g</span>
                      )}
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={readOnly}
                      onClick={() => setColumn(column, { open: true })}
                      className="border-dashed"
                    >
                      <Plus className="size-4" />
                      {addLabels[column]}
                    </Button>
                  )}
                </Cell>
              );
            })}

            {/* Yield */}
            <RowLabel>{t`Yield`}</RowLabel>
            {(["EACH", "WEIGHT", "LIQUID"] as const).map((column) => (
              <Cell key={column}>
                {isColumnSet(value, column) ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={value.columns[column].yieldPct}
                      onChange={(e) => setColumn(column, { yieldPct: e.target.value })}
                      inputMode="decimal"
                      placeholder="100"
                      disabled={readOnly}
                      className="h-11 w-20 text-right text-lg font-semibold"
                      aria-label={t`${columnNames[column]} yield percent`}
                    />
                    <span className="text-lg font-semibold">%</span>
                    {column === "WEIGHT" && <span className="text-sm text-(--color-text-secondary)">{t`Usable / cooked yield`}</span>}
                  </div>
                ) : (
                  <NotSet>{t`Not set until filled out`}</NotSet>
                )}
              </Cell>
            ))}

            {/* Density */}
            <RowLabel>
              <span className="flex items-center gap-1.5">
                {t`Density`}
                <DensityInfo />
              </span>
              <span className="text-xs font-normal text-(--color-text-secondary)">{t`Volume → weight`}</span>
            </RowLabel>
            <Cell>
              <NotSet>{t`Not needed`}</NotSet>
            </Cell>
            <Cell className="bg-(--color-surface-muted)">
              {isColumnSet(value, "WEIGHT") ? (
                <div className="flex flex-col gap-2 self-stretch">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">1</span>
                    <UnitSelect
                      value={value.densityRefCode}
                      onChange={(code) => onChange({ ...value, densityRefCode: code })}
                      units={options.densityRef}
                      disabled={readOnly}
                      label={t`Density reference unit`}
                    />
                    <span className="font-semibold">=</span>
                    <Input
                      value={value.densityGrams}
                      onChange={(e) => onChange({ ...value, densityGrams: e.target.value })}
                      inputMode="decimal"
                      placeholder={t`weigh it`}
                      disabled={readOnly}
                      className="h-11 w-24 text-lg font-semibold"
                      aria-label={t`Grams in the density reference unit`}
                    />
                    <span className="font-semibold">g</span>
                    {density !== null && !readOnly && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1 px-2 text-xs"
                        onClick={() => onChange({ ...value, densityGrams: "" })}
                      >
                        <X className="size-3.5" />
                        {t`Clear`}
                      </Button>
                    )}
                  </div>
                  <span className="text-sm text-(--color-text-secondary)">
                    {density !== null
                      ? t`${formatNumber(density, 3)} g per mL · 1 Tbsp = ${formatNumber(density * 14.7868, 1)} g · 1 cup = ${formatNumber(density * 236.588, 0)} g`
                      : t`Weigh 1 level ${uomLabelFor(value.densityRefCode, uoms)} of the prepped ingredient to unlock tsp, Tbsp and cups in recipes.`}
                  </span>
                </div>
              ) : (
                <NotSet>—</NotSet>
              )}
            </Cell>
            <Cell>
              <NotSet>{t`Not needed`}</NotSet>
            </Cell>

            {/* UoM in recipes */}
            <RowLabel>{t`UoM in recipes`}</RowLabel>
            {(["EACH", "WEIGHT", "LIQUID"] as const).map((column) => (
              <Cell key={column} className="justify-start">
                {isColumnSet(value, column) ? (
                  <RecipeUnitCell
                    column={column}
                    result={results[column]}
                    choices={recipeUnitChoices(column, options, column === "WEIGHT" && density !== null)}
                    onChange={(code) => setColumn(column, { recipeUomCode: code })}
                    readOnly={readOnly}
                    error={error(`${column.toLowerCase()}RecipeUomCode`)}
                  />
                ) : (
                  <NotSet>—</NotSet>
                )}
              </Cell>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function uomLabelFor(code: string, uoms: Uom[]): string {
  const uom = uoms.find((u) => u.code === code);
  return uom ? uomLabel(uom) : code;
}

function RecipeUnitCell({
  column,
  result,
  choices,
  onChange,
  readOnly,
  error,
}: {
  column: ConverterColumn;
  result: ColumnResult;
  choices: { uom: Uom; disabled: boolean }[];
  onChange: (code: string) => void;
  readOnly: boolean;
  error?: string;
}) {
  const { t } = useLingui();
  const weightGroup = choices.filter((c) => c.uom.type === "WEIGHT");
  const volumeGroup = choices.filter((c) => c.uom.type === "LIQUID");
  const grouped = column === "WEIGHT";

  return (
    <div className="flex flex-col gap-3 self-stretch">
      <Select value={result.recipeUom?.code ?? ""} onValueChange={onChange} disabled={readOnly}>
        <SelectTrigger className="h-11" aria-label={t`Recipe unit`}>
          <SelectValue placeholder={t`Choose…`} />
        </SelectTrigger>
        <SelectContent className={POPUP_LAYER}>
          {grouped ? (
            <>
              <SelectGroup>
                <SelectLabel>{t`Weight`}</SelectLabel>
                {weightGroup.map(({ uom }) => (
                  <SelectItem key={uom.code} value={uom.code}>
                    {uomLabel(uom)}
                  </SelectItem>
                ))}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel>{t`Volume (uses density)`}</SelectLabel>
                {volumeGroup.map(({ uom, disabled }) => (
                  <SelectItem key={uom.code} value={uom.code} disabled={disabled}>
                    {disabled ? t`${uomLabel(uom)} (add density)` : uomLabel(uom)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </>
          ) : (
            choices.map(({ uom }) => (
              <SelectItem key={uom.code} value={uom.code}>
                {uomLabel(uom)}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {error && <p className="text-sm text-(--color-danger-text)">{error}</p>}

      {grouped && result.steps.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {result.steps.map((step, i) => (
            <div key={i} className="flex justify-between gap-3 text-sm">
              <span className="font-mono text-(--color-text-secondary)">{step.formula}</span>
              <span className="font-semibold whitespace-nowrap">{step.result}</span>
            </div>
          ))}
        </div>
      )}

      {result.recipeUom && result.perCaseInRecipeUnit != null && (
        <div className="flex flex-col gap-0.5 rounded-xl bg-(--color-surface-sidebar) px-3.5 py-3 text-(--color-text-on-dark)">
          {grouped && <span className="text-xs text-(--color-text-on-dark)/70">{t`Conversion`}</span>}
          <span className="text-lg font-bold">{formatCaseResult(result.perCaseInRecipeUnit, result.recipeUom)}</span>
          {result.costPerRecipeUnit != null && (
            <span className="text-sm text-(--color-text-on-dark)/70">
              {t`${formatMoney(result.costPerRecipeUnit)} per ${uomLabel(result.recipeUom)}`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function DensityInfo() {
  const { t } = useLingui();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="icon" className="size-6 rounded-full" aria-label={t`What is density?`}>
          <Info className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[70] flex w-[22rem] flex-col gap-3 rounded-2xl border-none bg-(--color-surface-sidebar) p-4 text-(--color-text-on-dark)"
      >
        <span className="text-base font-bold">{t`What is density?`}</span>
        <span className="text-sm leading-snug text-(--color-text-on-dark)/85">
          {t`Density is how much a set volume of an ingredient weighs. You buy onions by the pound, but recipes often call for cups or tablespoons. Density connects the two.`}
        </span>
        <div className="flex flex-col gap-1.5 rounded-xl bg-(--color-text-on-dark)/10 px-3.5 py-3 text-sm">
          <span className="text-xs font-bold tracking-wide text-(--color-text-on-dark)/70 uppercase">{t`How to measure`}</span>
          <span>{t`1. Prep the ingredient the way the recipe uses it (diced, shredded).`}</span>
          <span>{t`2. Fill a level cup, Tbsp or tsp.`}</span>
          <span>{t`3. Weigh it in grams and enter the number.`}</span>
        </div>
        <span className="text-sm text-(--color-text-on-dark)/75">
          {t`Example: 1 cup diced onion = 160 g, so a recipe calling for 2 cups uses 320 g from the case.`}
        </span>
      </PopoverContent>
    </Popover>
  );
}

function UnitSelect({
  value,
  onChange,
  units,
  disabled,
  label,
}: {
  value: string;
  onChange: (code: string) => void;
  units: Uom[];
  disabled: boolean;
  label: string;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-11 w-24" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className={POPUP_LAYER}>
        {units.map((uom) => (
          <SelectItem key={uom.code} value={uom.code}>
            {uomLabel(uom)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ParseStatusPill({ status }: { status: "ok" | "ambiguous" | "error" | "empty" }) {
  const { t } = useLingui();
  if (status === "empty") return null;
  if (status === "ok") return <Badge variant="success">{t`✓ Read`}</Badge>;
  if (status === "ambiguous") return <Badge variant="warning">{t`Weight or liquid?`}</Badge>;
  return <Badge variant="neutral" className="px-2.5 py-0.5 text-xs">{t`Can't read`}</Badge>;
}

function LabeledField({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-sm font-semibold text-(--color-text-secondary)">{label}</span>
      {children}
      {error && <span className="text-sm text-(--color-danger-text)">{error}</span>}
    </label>
  );
}

function HeaderCell({ children, bordered = false }: { children?: ReactNode; bordered?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 bg-(--color-surface-muted) px-4 py-3 text-(--color-text-primary)",
        bordered && "border-l border-(--color-border)",
      )}
    >
      {children}
    </div>
  );
}

function RowLabel({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-t border-(--color-border) px-4 py-4 text-sm font-bold text-(--color-text-primary)">
      {children}
    </div>
  );
}

function Cell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col items-start justify-center border-t border-l border-(--color-border) px-4 py-4", className)}>
      {children}
    </div>
  );
}

function NotSet({ children }: { children: ReactNode }) {
  return <span className="text-sm text-(--color-text-secondary)">{children}</span>;
}
